// src/components/GatedWhitepaper.js

import React, { useState } from 'react';

// Helper to safely load dynamic images. 
// Requires images to be physically present in src/assets/ in formats: 1_image_1.png, 1_image_2.png, etc.
const safeImageLoad = (imageName) => {
  try {
    return require(`../assets/${imageName}.png`);
  } catch (err) {
    try {
      return require(`../assets/${imageName}.jpg`);
    } catch (e) {
      return null;
    }
  }
};

function GatedWhitepaper({ data }) {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ email: '', company: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { Slno, Date, Title, Para1, Para2, Para3, Para4, Para5, Author, Tags } = data;

  // Resolve dynamic image paths based on Slno
  const img1 = safeImageLoad(`${Slno}_image_1`);
  const img2 = safeImageLoad(`${Slno}_image_2`);
  const img3 = safeImageLoad(`${Slno}_image_3`);

  const tagList = Tags ? Tags.split(',').map(tag => tag.trim()) : [];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    const payload = {
      access_key: "de271c99-03f3-4ee1-bb87-140545d6ddbe", // Replace with your Web3Forms Access Key
      subject: `Whitepaper Unlocked: ${Title} - ${formData.company}`,
      from_name: formData.company,
      email: formData.email,
      requested_resource: Title
    };

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        setIsUnlocked(true);
        setShowForm(false);
      } else {
        setSubmitError("Failed to verify details. Please try again.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError("Network error. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Smart text parser to honor line breaks and auto-detect sub-headings from CSV
  const renderFormattedText = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    
    return lines.map((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={idx} style={{ height: '15px' }}></div>; // Empty lines create visual breaks

      // Heuristic: If a line is short and doesn't end with sentence punctuation, treat it as a Sub-Heading
      const isUpperCase = trimmed === trimmed.toUpperCase() && trimmed.match(/[A-Z]/);
      const isHeading = isUpperCase || (trimmed.length > 0 && trimmed.length < 80 && !['.', '?', '!'].includes(trimmed.slice(-1)));

      if (isHeading) {
        return <h4 key={idx} style={styles.subHeading}>{trimmed}</h4>;
      }
      return <p key={idx} style={styles.paragraphText}>{trimmed}</p>;
    });
  };

  if (!Title) return null; // Skip empty rows

  return (
    <div style={styles.container}>
      
      {/* Header Area */}
      <div style={styles.header}>
        <h2 style={styles.title}>{Title}</h2>
        <div style={styles.metaRow}>
          <span style={styles.metaText}>{Date}</span>
          <span style={styles.metaDivider}>|</span>
          <span style={styles.metaText}>By {Author}</span>
        </div>
        {tagList.length > 0 && (
          <div style={styles.tagsContainer}>
            {tagList.map((tag, idx) => (
              <span key={idx} style={styles.tag}>{tag}</span>
            ))}
          </div>
        )}
      </div>

      <div style={styles.content}>
        
        {/* Preview Content: Image 1 and Para 1 */}
        {img1 && (
          <div style={styles.imageContainer}>
            <img src={img1} alt={`Illustration 1 for ${Title}`} style={styles.fullImage} />
          </div>
        )}
        
        {Para1 && <div style={styles.textBlock}>{renderFormattedText(Para1)}</div>}

        {/* Gated Barrier Form */}
        {!isUnlocked && !showForm && (
          <div style={styles.gateAction}>
            <div style={styles.fadeOverlay}></div>
            <button onClick={() => setShowForm(true)} style={styles.primaryButton}>
              Read Fully &rarr;
            </button>
          </div>
        )}

        {!isUnlocked && showForm && (
          <div style={styles.formContainer}>
            <h4 style={styles.formTitle}>Access Full Whitepaper</h4>
            <p style={styles.formSubtitle}>Please provide your details to continue reading.</p>
            
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.inputRow}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Corporate Email*</label>
                  <input 
                    type="email" name="email" required 
                    value={formData.email} onChange={handleChange}
                    style={styles.input} placeholder="work@company.com" 
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Company Name*</label>
                  <input 
                    type="text" name="company" required 
                    value={formData.company} onChange={handleChange}
                    style={styles.input} placeholder="Enter company name" 
                  />
                </div>
              </div>

              {submitError && (
                <div style={styles.errorMessage}>
                  ⚠ {submitError}
                </div>
              )}

              <div style={styles.formActions}>
                <button type="button" onClick={() => setShowForm(false)} style={styles.cancelButton} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton} disabled={isSubmitting}>
                  {isSubmitting ? 'Unlocking...' : 'Unlock Content'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Unlocked Content */}
        {isUnlocked && (
          <div style={styles.unlockedContent}>
            
            {Para2 && <div style={styles.textBlock}>{renderFormattedText(Para2)}</div>}
            
            {(Para3 || img2) && (
              <div style={styles.wrapContainer}>
                {img2 && (
                  <img src={img2} alt={`Illustration 2 for ${Title}`} style={styles.leftFloatedImage} />
                )}
                {Para3 && <div>{renderFormattedText(Para3)}</div>}
              </div>
            )}
            
            {Para4 && <div style={styles.textBlock}>{renderFormattedText(Para4)}</div>}
            {Para5 && <div style={styles.textBlock}>{renderFormattedText(Para5)}</div>}

            {img3 && (
              <div style={styles.imageContainer}>
                <img src={img3} alt={`Illustration 3 for ${Title}`} style={styles.fullImage} />
              </div>
            )}
            
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    border: '1px solid #E5E7EB',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    marginBottom: '50px',
  },
  header: {
    backgroundColor: '#F9FAFB',
    padding: '40px 40px 25px 40px',
    borderBottom: '1px solid #E5E7EB',
    borderTop: '5px solid #002B49'
  },
  title: {
    fontSize: '32px',
    color: '#002B49',
    margin: '0 0 15px 0',
    fontWeight: 'bold',
    lineHeight: '1.3'
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px'
  },
  metaText: {
    fontSize: '15px',
    color: '#6B7280',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  metaDivider: {
    color: '#D1D5DB'
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px'
  },
  tag: {
    backgroundColor: '#FEE2E2',
    color: '#E1261C',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: 'bold'
  },
  content: {
    padding: '40px',
    position: 'relative'
  },
  imageContainer: {
    marginBottom: '25px',
    borderRadius: '12px',
    overflow: 'hidden'
  },
  fullImage: {
    width: '100%',
    height: 'auto',
    display: 'block'
  },
  textBlock: {
    marginBottom: '25px'
  },
  subHeading: {
    fontSize: '20px',
    color: '#002B49',
    fontWeight: 'bold',
    marginTop: '30px',
    marginBottom: '10px',
    lineHeight: '1.4'
  },
  paragraphText: {
    fontSize: '17px',
    color: '#4B5563',
    lineHeight: '1.8',
    marginBottom: '15px',
    textAlign: 'justify'
  },
  wrapContainer: {
    display: 'flow-root', 
    marginBottom: '25px'
  },
  leftFloatedImage: {
    float: 'left',
    width: '45%',
    minWidth: '250px',
    marginRight: '30px',
    marginBottom: '15px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
  },
  gateAction: {
    position: 'relative',
    marginTop: '-60px',
    paddingTop: '100px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  fadeOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100px',
    background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
    pointerEvents: 'none'
  },
  primaryButton: {
    backgroundColor: '#E1261C',
    color: '#FFFFFF',
    border: 'none',
    padding: '14px 35px',
    fontSize: '16px',
    fontWeight: 'bold',
    borderRadius: '8px',
    cursor: 'pointer',
    position: 'relative',
    zIndex: 1,
    boxShadow: '0 4px 10px rgba(225, 38, 28, 0.2)',
    transition: 'transform 0.2s ease'
  },
  formContainer: {
    marginTop: '20px',
    padding: '35px',
    backgroundColor: '#F9FAFB',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
    borderLeft: '4px solid #002B49'
  },
  formTitle: {
    fontSize: '22px',
    color: '#002B49',
    margin: '0 0 10px 0',
    fontWeight: 'bold'
  },
  formSubtitle: {
    fontSize: '15px',
    color: '#6B7280',
    margin: '0 0 25px 0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputRow: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap'
  },
  inputGroup: {
    flex: '1',
    minWidth: '220px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151'
  },
  input: {
    padding: '14px 16px',
    borderRadius: '8px',
    border: '1px solid #D1D5DB',
    fontSize: '15px',
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s ease'
  },
  errorMessage: {
    backgroundColor: '#FDE8E8',
    color: '#9B1C1C',
    padding: '12px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    marginTop: '10px'
  },
  formActions: {
    display: 'flex',
    gap: '15px',
    marginTop: '10px'
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    color: '#4B5563',
    border: '1px solid #D1D5DB',
    padding: '14px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  submitButton: {
    flex: 2,
    backgroundColor: '#002B49',
    color: '#FFFFFF',
    border: 'none',
    padding: '14px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  unlockedContent: {
    animation: 'fadeIn 0.5s ease-in',
    borderTop: '1px dashed #D1D5DB',
    paddingTop: '30px',
    marginTop: '30px'
  }
};

export default GatedWhitepaper;